import {
    ButtonGradientPrimary,
    ButtonSecondary,
    ConfirmDialog,
    Icon,
    ModalDialog,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { CareerStepForm } from './CareerStepForm';
import { CareerStepRow } from './CareerStepRow';
import { toCareerStepRequest, toFormValues, toRefusalMessage } from './careerStepValidation';
import { GROWTH_PLACEHOLDER } from './growthStyles';
import { readPositionName } from './targetPosition';

import type { CareerStepFormValues } from './careerStepValidation';
import type { CareerStep, CareerStepKind } from '@/types/career-growth';

import {
    useCreateCareerStep,
    useDeleteCareerStep,
    useUpdateCareerStep,
} from '@/api/performance-profile';
import { useAmelioTranslations } from '@/hooks';
import { useReportedWrite } from '@/hooks/utils/useReportedWrite';

interface CareerStepsEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    employeeId: string;
    /** The steps the tab already loaded; every write refreshes them through the growth query. */
    steps: CareerStep[];
    /** The competency profile assigned to the employee today, or `null` when there is none. */
    currentProfile: { id: string; name: string } | null;
    /** Opens straight on that kind's form — the pencil of the "next target" card, for instance. */
    initialEditKind?: CareerStepKind | null;
    dataTestId?: string;
}

// One card per step: its row, and while that step is being edited, its form underneath. The card
// carries the outline, so the row and the form never read as two blocks that happen to be near
// each other -- the open card is what says which step is being modified.
const STEP_CARD = 'rounded-xl border bg-white transition-colors';
const STEP_CARD_EDITING = 'border-primary/50 shadow-sm';
const STEP_CARD_IDLE = 'border-base-100';

/** Only one form is open at a time: either the new step, or the step being edited. */
type EditorForm = { mode: 'add'; kind: CareerStepKind } | { mode: 'edit'; stepId: string };

/** The kinds an employee can only hold once, so the editor never offers a second one. */
const UNIQUE_KINDS: CareerStepKind[] = ['Current', 'Target'];

/** A step's dates, newest first, drive the order of the list; a target with no horizon leads. */
const UNDATED_SORT_KEY = '9999';
const byMostRecentStart = (a: CareerStep, b: CareerStep) =>
    (b.startDate ?? UNDATED_SORT_KEY).localeCompare(a.startDate ?? UNDATED_SORT_KEY);

/**
 * Saving a step under another profile or kind clears the competencies added or removed for the
 * employee on it: the API keeps them only on the profile they were made for.
 */
const dropsAdjustments = (step: CareerStep, values: CareerStepFormValues) =>
    step.addedCompetencyIds.length + step.removedCompetencyIds.length > 0 &&
    (values.kind !== step.kind || values.competencyProfileId !== step.competencyProfileId);

/**
 * What the editor opens on: the step already holding `initialEditKind`, or a fresh form of that
 * kind when none does yet. Read once, at the modal's mount -- the caller remounts it with a new
 * `key` on every opening, so this never needs to react to a later change of its own.
 */
const initialFormFor = (
    initialEditKind: CareerStepKind | null,
    steps: CareerStep[],
): EditorForm | null => {
    if (!initialEditKind) {
        return null;
    }

    const stepOfKind = steps.find((step) => step.kind === initialEditKind);
    return stepOfKind
        ? { mode: 'edit', stepId: stepOfKind.id }
        : { mode: 'add', kind: initialEditKind };
};

/**
 * "Modifier les étapes de carrière": the list of the employee's career steps, each editable in
 * place, with one form open at a time. Every save is its own request — the modal never holds a
 * draft of the whole list, so closing it can never lose a step that was already saved.
 */
export const CareerStepsEditorModal = ({
    isOpen,
    onClose,
    employeeId,
    steps,
    currentProfile,
    initialEditKind = null,
    dataTestId = 'career-steps-editor',
}: CareerStepsEditorModalProps) => {
    const { t } = useAmelioTranslations();
    const runWrite = useReportedWrite();

    const { mutateAsync: createStep, isPending: isCreating } = useCreateCareerStep(employeeId);
    const { mutateAsync: updateStep, isPending: isUpdating } = useUpdateCareerStep(employeeId);
    const { mutateAsync: deleteStep, isPending: isDeleting } = useDeleteCareerStep(employeeId);

    // The caller mounts the modal with a fresh `key` on every opening (see `GrowthTab`), so this
    // initial value is read once per opening, and a save or a cancel is free to move `form` away
    // from it without that opening's kind creeping back.
    const [form, setForm] = useState<EditorForm | null>(() =>
        initialFormFor(initialEditKind, steps),
    );
    const [stepPendingDeletion, setStepPendingDeletion] = useState<CareerStep | null>(null);
    const [updatePendingConfirmation, setUpdatePendingConfirmation] = useState<{
        stepId: string;
        values: CareerStepFormValues;
    } | null>(null);

    const sortedSteps = useMemo(() => [...steps].sort(byMostRecentStart), [steps]);

    const stepByTakenKind = useMemo(
        () =>
            UNIQUE_KINDS.reduce<Partial<Record<CareerStepKind, CareerStep>>>((taken, kind) => {
                const step = steps.find((candidate) => candidate.kind === kind);
                return step ? { ...taken, [kind]: step } : taken;
            }, {}),
        [steps],
    );

    const handleCreate = async (values: CareerStepFormValues) => {
        const isSaved = await runWrite({
            write: () => createStep(toCareerStepRequest(values)),
            success: 'performanceProfile.growth.stepsEditor.added',
            failure: 'performanceProfile.growth.stepsEditor.saveError',
            explainRefusal: toRefusalMessage,
        });
        if (isSaved) {
            setForm(null);
        }
    };

    const saveUpdate = async (stepId: string, values: CareerStepFormValues) => {
        const isSaved = await runWrite({
            write: () => updateStep({ stepId, request: toCareerStepRequest(values) }),
            success: 'performanceProfile.growth.stepsEditor.updated',
            failure: 'performanceProfile.growth.stepsEditor.saveError',
            explainRefusal: toRefusalMessage,
        });
        if (isSaved) {
            setForm(null);
            setUpdatePendingConfirmation(null);
        }
    };

    const handleUpdate = (step: CareerStep) => async (values: CareerStepFormValues) => {
        if (dropsAdjustments(step, values)) {
            setUpdatePendingConfirmation({ stepId: step.id, values });
            return;
        }
        await saveUpdate(step.id, values);
    };

    const handleDelete = async () => {
        if (!stepPendingDeletion) {
            return;
        }

        const isDeleted = await runWrite({
            write: () => deleteStep(stepPendingDeletion.id),
            success: 'performanceProfile.growth.stepsEditor.deleted',
            failure: 'performanceProfile.growth.stepsEditor.deleteError',
        });
        if (isDeleted) {
            setStepPendingDeletion(null);
            if (form?.mode === 'edit' && form.stepId === stepPendingDeletion.id) {
                setForm(null);
            }
        }
    };

    // "Add step" proposes the kind the editor was opened for (the target, from the desired position)
    // while that kind is still free; from the career path it proposes a past position, the one kind
    // that can always be added.
    const newStepKind: CareerStepKind =
        initialEditKind && !stepByTakenKind[initialEditKind] ? initialEditKind : 'Past';

    const addDefaults =
        form?.mode === 'add'
            ? toFormValues(null, {
                  kind: form.kind,
                  competencyProfileId:
                      form.kind === 'Current' ? (currentProfile?.id ?? null) : null,
              })
            : null;

    const isListEmpty = sortedSteps.length === 0 && !form;

    return (
        <>
            <ModalDialog
                isOpen={isOpen}
                onClose={onClose}
                closeAriaLabel={t('common.close')}
                title={t('performanceProfile.growth.stepsEditor.title')}
                subtitle={t('performanceProfile.growth.stepsEditor.subtitle')}
                size={'md'}
                dataTestId={dataTestId}
                radius={'4xl'}
                padding={'xl'}
                shadow={'2xl'}
                bordered
                backdropBlur
                footer={
                    <div className={'flex items-center justify-end gap-2'}>
                        {form ? null : (
                            <ButtonGradientPrimary
                                size={'sm'}
                                className={'mr-auto'}
                                onClick={() => setForm({ mode: 'add', kind: newStepKind })}
                                leftIcon={
                                    <Icon
                                        name={'essential-add'}
                                        size={'sm'}
                                        ariaHidden
                                    />
                                }
                                dataTestId={`${dataTestId}-add`}
                            >
                                {t('performanceProfile.growth.stepsEditor.addStep')}
                            </ButtonGradientPrimary>
                        )}

                        <ButtonSecondary
                            size={'sm'}
                            onClick={onClose}
                            dataTestId={`${dataTestId}-close-footer`}
                        >
                            {t('common.close')}
                        </ButtonSecondary>
                    </div>
                }
            >
                <div className={'flex flex-col gap-3'}>
                    {isListEmpty ? (
                        <p className={GROWTH_PLACEHOLDER}>
                            {t('performanceProfile.growth.stepsEditor.empty')}
                        </p>
                    ) : null}

                    {addDefaults ? (
                        <CareerStepForm
                            heading={t('performanceProfile.growth.stepsEditor.newStep')}
                            submitLabel={t('common.add')}
                            defaultValues={addDefaults}
                            currentProfile={currentProfile}
                            stepByTakenKind={stepByTakenKind}
                            isSaving={isCreating}
                            onSubmit={handleCreate}
                            onCancel={() => setForm(null)}
                            dataTestId={`${dataTestId}-new-form`}
                        />
                    ) : null}

                    {sortedSteps.map((step) => {
                        const isEditing = form?.mode === 'edit' && form.stepId === step.id;

                        return (
                            <div
                                key={step.id}
                                className={`${STEP_CARD} ${isEditing ? STEP_CARD_EDITING : STEP_CARD_IDLE}`}
                                data-testid={`${dataTestId}-card-${step.id}`}
                            >
                                <CareerStepRow
                                    step={step}
                                    onEdit={() => setForm({ mode: 'edit', stepId: step.id })}
                                    onDelete={() => setStepPendingDeletion(step)}
                                    dataTestId={`${dataTestId}-step-${step.id}`}
                                />

                                {isEditing ? (
                                    <CareerStepForm
                                        heading={t(
                                            'performanceProfile.growth.stepsEditor.editStep',
                                        )}
                                        submitLabel={t('common.save')}
                                        defaultValues={toFormValues(step, {
                                            kind: step.kind,
                                            competencyProfileId: step.competencyProfileId,
                                        })}
                                        currentProfile={currentProfile}
                                        stepByTakenKind={stepByTakenKind}
                                        isSaving={isUpdating}
                                        onSubmit={handleUpdate(step)}
                                        onCancel={() => setForm(null)}
                                        surface={'inline'}
                                        dataTestId={`${dataTestId}-edit-form`}
                                    />
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </ModalDialog>

            <ConfirmDialog
                isOpen={!!stepPendingDeletion}
                title={t('performanceProfile.growth.stepsEditor.deleteTitle')}
                message={t('performanceProfile.growth.stepsEditor.deleteMessage', {
                    title: stepPendingDeletion ? readPositionName(stepPendingDeletion) : '',
                })}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={handleDelete}
                onCancel={() => setStepPendingDeletion(null)}
                isLoading={isDeleting}
                isDestructive
                // Above the editor it is opened from, which the library `Modal` puts at 50.
                zIndex={60}
                dataTestId={`${dataTestId}-delete-confirm`}
            />

            <ConfirmDialog
                isOpen={!!updatePendingConfirmation}
                title={t('performanceProfile.growth.stepsEditor.clearAdjustmentsTitle')}
                message={t('performanceProfile.growth.stepsEditor.clearAdjustmentsMessage')}
                confirmLabel={t('common.confirm')}
                cancelLabel={t('common.cancel')}
                onConfirm={async () => {
                    if (updatePendingConfirmation) {
                        await saveUpdate(
                            updatePendingConfirmation.stepId,
                            updatePendingConfirmation.values,
                        );
                    }
                }}
                onCancel={() => setUpdatePendingConfirmation(null)}
                isLoading={isUpdating}
                zIndex={60}
                dataTestId={`${dataTestId}-clear-adjustments-confirm`}
            />
        </>
    );
};
